import {
  type PropsWithChildren,
  useMemo,
  type ReactNode,
  Children,
} from 'react';

import {
  isDataTableCellActions,
  type CollectedCellActions,
} from './CellActions.js';
import {
  isDataTableColumnActions,
  type CollectedColumnActions,
} from './ColumnActions.js';
import { isDataTableRowActions } from './RowActions.js';
import type { TableOptions } from '../../hooks/useTable/types.js';
import type { DataTableColumnDef, DataTableRowData } from '../../public.api.js';

/**
 * Configuration hook for the DataTable UserActions feature
 * @internal
 */
export function useUserActions<TData extends DataTableRowData>(
  props: PropsWithChildren,
  options: TableOptions<TData>,
  consumerColumnDefMap: Map<string, DataTableColumnDef<TData>>,
): void {
  const configuredActions = useMemo(() => {
    const actions: {
      cellActions: CollectedCellActions<TData>;
      columnActions: CollectedColumnActions<TData>;
      rowActions?: (
        row: TData,
        {
          rowDensity,
          rowIndex,
          rowId,
        }: {
          rowDensity: 'default' | 'condensed' | 'comfortable';
          rowIndex: number;
          rowId: string;
        },
      ) => ReactNode;
    } = {
      cellActions: [],
      columnActions: [],
      rowActions: undefined,
    };

    // Iterate through the children of the DataTable component in order to
    // find the `ColumnActions`, `CellActions` and `RowActions` definitions.
    Children.forEach(props.children, (child) => {
      // Check if the child is of type cell action and insert it into the actions
      // configuration.
      if (isDataTableCellActions<TData>(child)) {
        // Add the action into the cell actions collection saved in the options.
        actions.cellActions.push({
          column: child.props.column,
          action: child.props.children,
        });
      }
      if (isDataTableColumnActions<TData>(child)) {
        // Add the action into the column actions collection saved in the options.
        actions.columnActions.push({
          column: child.props.column,
          action: child.props.children,
        });
      }
      if (isDataTableRowActions(child)) {
        actions.rowActions = child.props.children;
      }
    });
    return actions;
  }, [props.children]);

  options.consumerColumnDefMap = consumerColumnDefMap;
  options.enableColumnActions = configuredActions.columnActions.length > 0;
  options.columnActions = configuredActions.columnActions;

  options.enableCellActions = configuredActions.cellActions.length > 0;
  options.cellActions = configuredActions.cellActions;

  options.enableRowActions = configuredActions.rowActions !== undefined;
  options.rowActions = configuredActions.rowActions;
}
